import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useInsuranceApi } from '../../../../hooks/use-insurance-api';
import { PatientVerificationSummary } from '../../../../services/insurance/types/patient-verification-summary';
import { VerificationSummary } from '../../../../services/insurance/types/verification-summary';
import { EventIvShowVerificationDialog } from '../../../../types/event-bus';
import InsuranceInactiveStatusDialog from './insurance-inactive/insurance-inactive-dialog';
import InsuranceTermedStatusDialog from './insurance-termed/insurance-termed-dialog';
import InsuranceVerifiedStatusDialog from './insurance-verified/insurance-verified-dialog';
import ManuallyVerifiedStatusDialog from './manually-verified/manually-verified-dialog';
import InaccurateDataStatusDialog from './needs-attention/inaccurate-data/inaccurate-data-dialog';
import NotRegisteredWithPayerStatusDialog from './needs-attention/not-registered-with-payer/not-registered-with-payer';
import NpiInvalidStatusDialog from './needs-attention/npi-invalid/npi-invalid-dialog';
import NpiMissingStatusDialog from './needs-attention/npi-missing/npi-missing-dialog';
import PayerUnableToRespondStatusDialog from './needs-attention/payer-unable-to-respond/payer-unable-to-respond-dialog';
import ProviderRelationshipInvalidStatusDialog from './needs-attention/provider-relationship-invalid/provider-relationship-invalid-dialog';
import UnknownPatientStatusDialog from './needs-attention/unknown-patient-status/unknown-patient-status-dialog';
import PatientDataMissingStatusDialog from './patient-data-missing/patient-data-missing-dialog';
import PayerNotSetupStatusDialog from './payer-not-setup/payer-not-setup-dialog';
import RequestDataInvalidStatusDialog from './request-data-invalid/request-data-invalid-dialog';
import RetryInProgressStatusDialog from './retry-in-progress/retry-in-progress-dialog';
import SelfPayStatusDialog from './self-pay/self-pay-dialog';
import VerificationInProcessStatusDialog from './verification-in-process/verification-in-process-dialog';

export interface PatientIvStatusDialogSelectorProps {
  opened: boolean;
  patientInsuranceEvent: EventIvShowVerificationDialog;
  onClose: () => void;
}

export const PatientIvStatusDialogSelector: FC<PatientIvStatusDialogSelectorProps> = (props) => {
  const { opened, patientInsuranceEvent, onClose } = props;
  const { getPatientVerificationSummaries } = useInsuranceApi();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIvSummary, setSelectedIvSummary] = useState<VerificationSummary>();
  const [patientIvSummary, setPatientIvSummary] = useState<PatientVerificationSummary>();

  const loadPatientIvSummaries = useCallback(async () => {
    setIsLoading(true);

    try {
      const eventData = patientInsuranceEvent.data;

      const summaries = await getPatientVerificationSummaries(
        [eventData.patientId],
        eventData.locationId,
        eventData.providerId
      );

      if (summaries.length === 0) return;

      const patientSummary = eventData.providerId
        ? summaries.find((x) =>
            eventData.priority === 'Primary'
              ? x.primaryVerificationSummary?.idProvider === eventData.providerId
              : x.secondaryVerificationSummary?.idProvider === eventData.providerId
          )
        : summaries[0];

      setPatientIvSummary(patientSummary);
      setSelectedIvSummary(
        eventData.priority === 'Primary'
          ? patientSummary?.primaryVerificationSummary
          : patientSummary?.secondaryVerificationSummary
      );
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  }, [patientInsuranceEvent]);

  useEffect(() => {
    if (patientInsuranceEvent) loadPatientIvSummaries();
  }, [patientInsuranceEvent]);

  const compoundSelectedIvSummary = useMemo(() => {
    if (isLoading) return { ivSummaryStatus: 'Unknown' } as VerificationSummary;

    if (!selectedIvSummary) return { ivSummaryStatus: 'PatientDataMissing' } as VerificationSummary;

    return selectedIvSummary;
  }, [selectedIvSummary, isLoading]);

  const dialogProps = {
    opened,
    onClose,
    selectedIvSummary: compoundSelectedIvSummary,
    patientFlow: {
      patientId: patientInsuranceEvent.data.patientId,
      verificationPriority: patientInsuranceEvent.data.priority,
      patientIvSummary
    },
    locationId: patientInsuranceEvent.data.locationId,
    providerId: patientInsuranceEvent.data.providerId,
    appointment: patientInsuranceEvent.data.appointment
  };

  switch (compoundSelectedIvSummary?.ivSummaryStatus) {
    case 'InsuranceInProcess':
      return <VerificationInProcessStatusDialog {...dialogProps} />;
    case 'InsuranceVerified':
      return <InsuranceVerifiedStatusDialog {...dialogProps} />;
    case 'SelfPay':
      return <SelfPayStatusDialog {...dialogProps} />;
    case 'ManuallyVerified':
      return <ManuallyVerifiedStatusDialog {...dialogProps} />;
    case 'InsuranceInactive':
      return <InsuranceInactiveStatusDialog {...dialogProps} />;
    case 'InsuranceTermed':
      return <InsuranceTermedStatusDialog {...dialogProps} />;
    case 'RetryInProgress':
      return <RetryInProgressStatusDialog {...dialogProps} />;
    case 'PatientDataMissing':
      return <PatientDataMissingStatusDialog {...dialogProps} />;
    case 'RequestDataInvalid':
      return <RequestDataInvalidStatusDialog {...dialogProps} />;
    case 'PayerNotSetup':
      return <PayerNotSetupStatusDialog {...dialogProps} />;
    case 'NpiInvalid':
      return <NpiInvalidStatusDialog {...dialogProps} />;
    case 'NpiMissing':
      return <NpiMissingStatusDialog {...dialogProps} />;
    case 'ProviderRelationshipInvalid':
      return <ProviderRelationshipInvalidStatusDialog {...dialogProps} />;
    case 'NotRegisteredWithPayer':
      return <NotRegisteredWithPayerStatusDialog {...dialogProps} />;
    case 'PayerUnableToRespond':
      return <PayerUnableToRespondStatusDialog {...dialogProps} />;
    case 'InaccurateData':
      return <InaccurateDataStatusDialog {...dialogProps} />;
    case 'UnknownPatient':
      return <UnknownPatientStatusDialog {...dialogProps} />;
    default:
      return null;
  }
};
